import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  InputAdornment,
  List,
  ListItem,
  ListItemText,
  Snackbar,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import {
  QrCodeScanner as QrIcon,
  Search as SearchIcon,
  PersonAdd as PersonAddIcon,
  Redeem as RedeemIcon,
  ShoppingCart as CartIcon,
  OpenInNew as OpenIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import LoyaltyService from '../../services/loyalty.service';
import LocationDataService from '../../services/location.service';
import { useTranslation } from '../../hooks/useTranslation';
import { useAuth } from '../../contexts/AuthContext';
import LocationSelect from './LocationSelect';
import { apiError, formatDate, formatMoney, formatPoints, preselectLocation } from './loyaltyUtils';

/**
 * Cashier flow: scan/search customer -> see balance -> register purchase
 * or redeem a reward at a location.
 */
const LoyaltyCashier = () => {
  const { t } = useTranslation();
  const { user, hasPermission } = useAuth();
  const navigate = useNavigate();

  const [locations, setLocations] = useState([]);
  const [locationId, setLocationId] = useState('');
  const [rewards, setRewards] = useState([]);
  const [pointsPerCurrency, setPointsPerCurrency] = useState(1);

  const [query, setQuery] = useState('');
  const [searching, setSearching] = useState(false);
  const [client, setClient] = useState(null);       // global identity
  const [membership, setMembership] = useState(null); // this tenant's balance
  const [notFound, setNotFound] = useState(false);

  const [amount, setAmount] = useState('');
  const [reference, setReference] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);

  const [enrollOpen, setEnrollOpen] = useState(false);
  const [enrollForm, setEnrollForm] = useState({ first_name: '', last_name: '', phone: '', email: '' });

  const [redeemOpen, setRedeemOpen] = useState(false);
  const [redeeming, setRedeeming] = useState(null);

  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const showSnackbar = (message, severity = 'success') => setSnackbar({ open: true, message, severity });

  const canCreate = hasPermission('loyalty.create');

  useEffect(() => {
    (async () => {
      try {
        const [locRes, rewardRes, settingsRes] = await Promise.all([
          LocationDataService.getAll(),
          LoyaltyService.getRewards({ available: 1 }),
          LoyaltyService.getSettings(),
        ]);
        const locs = locRes.data?.data || [];
        setLocations(locs);
        const pre = preselectLocation(locs);
        if (pre) setLocationId(pre.id);
        setRewards(rewardRes.data?.data || []);
        setPointsPerCurrency(parseFloat(settingsRes.data?.data?.points_per_currency ?? 1));
      } catch (error) {
        showSnackbar(apiError(error, t('loyalty.errorLoading')), 'error');
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const pointsPreview = useMemo(() => {
    const n = parseFloat(amount);
    if (!n || n <= 0) return 0;
    return Math.floor(n * pointsPerCurrency);
  }, [amount, pointsPerCurrency]);

  const lookup = useCallback(async (q) => {
    const term = (q ?? query).trim();
    if (!term) return;
    setSearching(true);
    setNotFound(false);
    setResult(null);
    try {
      const res = await LoyaltyService.lookupClient(term);
      setClient(res.data?.data?.client || null);
      setMembership(res.data?.data?.membership || null);
    } catch (error) {
      if (error.response?.status === 404) {
        setClient(null);
        setMembership(null);
        setNotFound(true);
        setEnrollForm((f) => ({ ...f, phone: /^\+?[\d\s-]+$/.test(term) ? term : '' }));
      } else {
        showSnackbar(apiError(error, t('loyalty.errorLoading')), 'error');
      }
    } finally {
      setSearching(false);
    }
  }, [query, t]);

  const refreshMembership = async (uuid) => {
    try {
      const res = await LoyaltyService.getClientLoyalty(uuid);
      setMembership(res.data?.data || null);
    } catch (e) {
      // membership may not exist yet; ignore
    }
  };

  const handleEnroll = async () => {
    if (!enrollForm.first_name || !enrollForm.phone) {
      showSnackbar(t('common.snackbar.pleaseFillAllRequiredFields'), 'error');
      return;
    }
    try {
      setSubmitting(true);
      const res = await LoyaltyService.enrollClient(enrollForm);
      const data = res.data?.data;
      setClient({ uuid: data.uuid, first_name: data.first_name, last_name: data.last_name, full_name: data.full_name, phone: data.phone, email: data.email, is_birthday_today: data.is_birthday_today });
      setMembership(data);
      setNotFound(false);
      setEnrollOpen(false);
      setEnrollForm({ first_name: '', last_name: '', phone: '', email: '' });
      showSnackbar(t('loyalty.clientEnrolled'));
    } catch (error) {
      showSnackbar(apiError(error, t('loyalty.errorSaving')), 'error');
    } finally {
      setSubmitting(false);
    }
  };

  const handlePurchase = async () => {
    if (!client || !locationId || !amount || parseFloat(amount) <= 0) {
      showSnackbar(t('common.snackbar.pleaseFillAllRequiredFields'), 'error');
      return;
    }
    try {
      setSubmitting(true);
      const res = await LoyaltyService.registerPurchase(client.uuid, {
        location_id: locationId,
        purchase_amount: parseFloat(amount),
        external_reference: reference || null,
      });
      const data = res.data?.data;
      setResult({ type: 'purchase', points: data.points_earned, balance: data.new_balance });
      setMembership(data.membership);
      setAmount('');
      setReference('');
    } catch (error) {
      showSnackbar(apiError(error, t('loyalty.errorSaving')), 'error');
    } finally {
      setSubmitting(false);
    }
  };

  const handleRedeem = async (reward) => {
    if (!client || !locationId) return;
    try {
      setRedeeming(reward.uuid);
      const res = await LoyaltyService.redeemReward(reward.uuid, {
        loyalty_client_uuid: client.uuid,
        location_id: locationId,
        idempotency_key: `${client.uuid}-${reward.uuid}-${Date.now()}`,
      });
      const data = res.data?.data;
      setResult({ type: 'redeem', points: -data.points_spent, balance: data.new_balance, reward: reward.name });
      setRedeemOpen(false);
      await refreshMembership(client.uuid);
    } catch (error) {
      showSnackbar(apiError(error, t('loyalty.errorSaving')), 'error');
    } finally {
      setRedeeming(null);
    }
  };

  const reset = () => {
    setClient(null);
    setMembership(null);
    setNotFound(false);
    setResult(null);
    setQuery('');
  };

  const balance = membership?.points_balance ?? 0;

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" fontWeight="bold">{t('loyalty.cashierTitle')}</Typography>
        <Chip label={`${user?.tenant?.name || ''}`} variant="outlined" />
      </Box>

      <Grid container spacing={3}>
        {/* Search / scan */}
        <Grid size={{ xs: 12, md: 5 }}>
          <Card sx={{ borderRadius: 2 }}>
            <CardHeader avatar={<QrIcon color="primary" />} title={t('loyalty.findCustomer')} />
            <CardContent>
              <TextField
                fullWidth
                autoFocus
                label={t('loyalty.scanOrSearch')}
                placeholder={t('loyalty.scanPlaceholder')}
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') lookup(); }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton onClick={() => lookup()} disabled={searching}>
                        {searching ? <CircularProgress size={20} /> : <SearchIcon />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
              <Typography variant="caption" color="text.secondary">{t('loyalty.scanHint')}</Typography>

              {notFound && (
                <Alert
                  severity="info"
                  sx={{ mt: 2 }}
                  action={canCreate && (
                    <Button color="inherit" size="small" startIcon={<PersonAddIcon />} onClick={() => setEnrollOpen(true)}>
                      {t('loyalty.enroll')}
                    </Button>
                  )}
                >
                  {t('loyalty.customerNotFound')}
                </Alert>
              )}

              {client && (
                <Box mt={3}>
                  <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                    <Box>
                      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                        <Typography variant="h5" fontWeight="bold">{client.full_name || `${client.first_name} ${client.last_name || ''}`}</Typography>
                        {client.is_birthday_today && <Chip size="small" color="secondary" label={t('loyalty.birthdayToday')} />}
                      </Stack>
                      <Typography variant="body2" color="text.secondary">{client.phone}{client.email ? ` · ${client.email}` : ''}</Typography>
                    </Box>
                    <Stack direction="row" spacing={0.5}>
                      {membership && (
                        <IconButton size="small" onClick={() => navigate(`/loyalty/clients/${client.uuid}`)} title={t('loyalty.viewProfile')}>
                          <OpenIcon fontSize="small" />
                        </IconButton>
                      )}
                      <IconButton size="small" onClick={reset}><CloseIcon fontSize="small" /></IconButton>
                    </Stack>
                  </Box>

                  <Divider sx={{ my: 2 }} />

                  {membership ? (
                    <Grid container spacing={2}>
                      <Grid size={{ xs: 4 }}>
                        <Typography variant="caption" color="text.secondary">{t('loyalty.currentPoints')}</Typography>
                        <Typography variant="h4" fontWeight="bold" color="primary.main">{formatPoints(balance)}</Typography>
                      </Grid>
                      <Grid size={{ xs: 4 }}>
                        <Typography variant="caption" color="text.secondary">{t('loyalty.visits')}</Typography>
                        <Typography variant="h5" fontWeight="bold">{membership.visit_count}</Typography>
                      </Grid>
                      <Grid size={{ xs: 4 }}>
                        <Typography variant="caption" color="text.secondary">{t('loyalty.lastVisit')}</Typography>
                        <Typography variant="body1">{formatDate(membership.last_visit_at)}</Typography>
                      </Grid>
                    </Grid>
                  ) : (
                    <Alert severity="info">{t('loyalty.notMemberYet')}</Alert>
                  )}
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Purchase */}
        <Grid size={{ xs: 12, md: 7 }}>
          <Card sx={{ borderRadius: 2, mb: 3 }}>
            <CardHeader avatar={<CartIcon color="primary" />} title={t('loyalty.registerPurchase')} />
            <CardContent>
              {result && (
                <Alert severity="success" sx={{ mb: 2 }} onClose={() => setResult(null)}>
                  <Typography fontWeight="bold">
                    {result.type === 'purchase' ? t('loyalty.purchaseRegistered') : `${t('loyalty.rewardRedeemed')}: ${result.reward}`}
                  </Typography>
                  <Typography variant="h5">{result.points > 0 ? '+' : ''}{formatPoints(result.points)} {t('loyalty.points')}</Typography>
                  <Typography>{t('loyalty.newBalance')}: <b>{formatPoints(result.balance)} {t('loyalty.points')}</b></Typography>
                </Alert>
              )}

              <Grid container spacing={2}>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <LocationSelect locations={locations} value={locationId} onChange={setLocationId} />
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    fullWidth
                    required
                    type="number"
                    label={t('loyalty.purchaseTotal')}
                    value={amount}
                    onChange={(e) => setAmount(e.target.value)}
                    inputProps={{ min: 0, step: '0.01' }}
                    InputProps={{ startAdornment: <InputAdornment position="start">{formatMoney(0).replace(/[\d.,\s]/g, '')}</InputAdornment> }}
                    disabled={!client}
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    fullWidth
                    label={t('loyalty.reference')}
                    value={reference}
                    onChange={(e) => setReference(e.target.value)}
                    disabled={!client}
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <Box sx={{ p: 1.5, bgcolor: 'action.hover', borderRadius: 1 }}>
                    <Typography variant="caption" color="text.secondary">{t('loyalty.pointsEarned')}</Typography>
                    <Typography variant="h5" fontWeight="bold" color="success.main">+{formatPoints(pointsPreview)}</Typography>
                    <Typography variant="caption" color="text.secondary">{t('loyalty.rule')}: {formatMoney(1)} = {pointsPerCurrency} {t('loyalty.points')}</Typography>
                  </Box>
                </Grid>
              </Grid>

              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} mt={3}>
                <Button
                  variant="contained"
                  size="large"
                  fullWidth
                  startIcon={submitting ? <CircularProgress size={18} color="inherit" /> : <CartIcon />}
                  disabled={!canCreate || !client || !locationId || submitting || !(parseFloat(amount) > 0)}
                  onClick={handlePurchase}
                >
                  {t('loyalty.registerPurchase')}
                </Button>
                <Button
                  variant="outlined"
                  size="large"
                  fullWidth
                  startIcon={<RedeemIcon />}
                  disabled={!canCreate || !client || !membership || !locationId}
                  onClick={() => setRedeemOpen(true)}
                >
                  {t('loyalty.redeemReward')}
                </Button>
              </Stack>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Enroll dialog */}
      <Dialog open={enrollOpen} onClose={() => setEnrollOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{t('loyalty.enrollCustomer')}</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 0 }}>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField fullWidth required label={t('loyalty.firstName')} value={enrollForm.first_name} onChange={(e) => setEnrollForm({ ...enrollForm, first_name: e.target.value })} />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField fullWidth label={t('loyalty.lastName')} value={enrollForm.last_name} onChange={(e) => setEnrollForm({ ...enrollForm, last_name: e.target.value })} />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField fullWidth required label={t('common.phone')} value={enrollForm.phone} onChange={(e) => setEnrollForm({ ...enrollForm, phone: e.target.value })} helperText={t('loyalty.phoneHint')} />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField fullWidth type="email" label={t('common.email')} value={enrollForm.email} onChange={(e) => setEnrollForm({ ...enrollForm, email: e.target.value })} />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEnrollOpen(false)}>{t('common.cancel')}</Button>
          <Button variant="contained" onClick={handleEnroll} disabled={submitting}>{t('loyalty.enroll')}</Button>
        </DialogActions>
      </Dialog>

      {/* Redeem dialog */}
      <Dialog open={redeemOpen} onClose={() => setRedeemOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{t('loyalty.redeemReward')} — {formatPoints(balance)} {t('loyalty.points')}</DialogTitle>
        <DialogContent>
          {rewards.length === 0 && <Alert severity="info">{t('loyalty.noRewards')}</Alert>}
          <List>
            {rewards.map((r) => {
              const affordable = balance >= r.points_required;
              return (
                <ListItem
                  key={r.uuid}
                  secondaryAction={
                    <Button
                      variant={affordable ? 'contained' : 'outlined'}
                      size="small"
                      disabled={!affordable || !!redeeming}
                      onClick={() => handleRedeem(r)}
                    >
                      {redeeming === r.uuid ? <CircularProgress size={16} /> : t('loyalty.redeem')}
                    </Button>
                  }
                >
                  <ListItemText
                    primary={r.name}
                    secondary={`${formatPoints(r.points_required)} ${t('loyalty.points')}${r.description ? ` · ${r.description}` : ''}`}
                  />
                </ListItem>
              );
            })}
          </List>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRedeemOpen(false)}>{t('common.close')}</Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={snackbar.open} autoHideDuration={6000} onClose={() => setSnackbar({ ...snackbar, open: false })}>
        <Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity}>{snackbar.message}</Alert>
      </Snackbar>
    </Box>
  );
};

export default LoyaltyCashier;
